/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import React, {CSSProperties, MouseEventHandler, useRef} from 'react';

import {useTranslation} from 'react-i18next';

import './style/emailStepStats.scss';

import {ActivateHandle, DashboardPeriod, DataSplitMethod, EmailStepStat} from '../../../util/interfaces';

import {AwesomeIcon, ChangeFunction, CloseButton, Loader, SelectItemShape, WithBackground} from '@dvrd/dvr-controls';

import {BluemateButton, BluemateSelect} from '../../controls';

import PeriodPicker from '../../dashboard/periodPicker/periodPicker';

import classNames from 'classnames';

import {parseThousandSeparator} from '../../../util/utils';

interface Props {
    onClose: MouseEventHandler;
    onChangePeriod: ChangeFunction<DashboardPeriod>;
    onChangeSplitMethod: ChangeFunction<DataSplitMethod>;
    stats: EmailStepStat[];
    active: boolean;
    loading: boolean;
    period: DashboardPeriod | null;
    splitMethod: DataSplitMethod;
    showColors: boolean;
}

const AVERAGE_CTR = 2.3;

export default function EmailStepStats(props: Props) {
    const {t} = useTranslation();
    const {stats, loading, active, splitMethod, period, onChangePeriod, onChangeSplitMethod, onClose, showColors} =
        props;
    const methodItems: SelectItemShape[] = [
        {label: t('email.stats.splitMethod.day', 'Per dag'), value: DataSplitMethod.DAY},
        {label: t('email.stats.splitMethod.week', 'Per week'), value: DataSplitMethod.WEEK},
        {label: t('email.stats.splitMethod.month', 'Per maand'), value: DataSplitMethod.MONTH},
        {label: t('email.stats.splitMethod.quarter', 'Per kwartaal'), value: DataSplitMethod.QUARTER},
        {label: t('email.stats.splitMethod.year', 'Per jaar'), value: DataSplitMethod.YEAR}
    ];
    const periodPicker = useRef<ActivateHandle>(null);

    function onClickPeriod() {
        periodPicker.current?.activate();
    }

    function getPeriodHeadLabel() {
        switch (splitMethod) {
            case DataSplitMethod.WEEK:
                return t('email.stats.head.week', 'Week');
            case DataSplitMethod.MONTH:
                return t('email.stats.head.month', 'Maand');
            case DataSplitMethod.QUARTER:
                return t('email.stats.head.quarter', 'Kwartaal');
            case DataSplitMethod.YEAR:
                return t('email.stats.head.year', 'Jaar');
            default:
                return t('email.stats.head.date', 'Datum');
        }
    }

    function calcPercentage(variable: number, comparedTo: number): string {
        if (!variable || !comparedTo) return '0';
        return ((variable / comparedTo) * 100).toFixed(2);
    }

    function getCTRStyle(ctrPercentage: number): CSSProperties | undefined {
        if (!showColors) return undefined;
        let color: string,
            fontWeight = 400;
        if (ctrPercentage > AVERAGE_CTR) {
            color = '#18A155'; // Green
            fontWeight = 600;
        } else if (ctrPercentage >= AVERAGE_CTR) color = '#18A155';
        else color = '#f39320';
        return {color, fontWeight};
    }

    function renderStats() {
        return (
            <div className='stats-container'>
                <Loader active={loading} />
                <div className='row head'>
                    <label>{getPeriodHeadLabel()}</label>
                    <label className='center'>{t('email.stats.mailsSent', 'Aantal mails verzonden')}</label>
                    <label className='center'>{t('email.stats.mailsOpened', 'Aantal mails geopend')}</label>
                    {showColors ? (
                        renderWithInfo(
                            t('email.stats.reviewLinksClicked', 'Aantal reviewlinks geklikt'),
                            `${t('email.stats.avgCtrPrefix', 'In 2021 was wereldwijd de gemiddelde klik rate')} ${AVERAGE_CTR}${t('email.stats.avgCtrSuffix', '% voor marketing mails.')}`,
                            true
                        )
                    ) : (
                        <label className='center'>
                            {t('email.stats.reviewLinksClicked', 'Aantal reviewlinks geklikt')}
                        </label>
                    )}
                </div>
                {stats.map(renderStat)}
            </div>
        );
    }

    function renderWithInfo(title: string, text: string, positionLeft: boolean = false) {
        return (
            <div className='with-info center'>
                <label>{title}</label>
                <div className='info-container'>
                    <AwesomeIcon name='info-circle' className='info-icon' />
                    <p className={classNames('info-text', positionLeft && 'left')}>{text}</p>
                </div>
            </div>
        );
    }

    function renderStat(stat: EmailStepStat, index: number) {
        const ctrPercentage = calcPercentage(stat.CLICK, stat.OPEN);
        return (
            <div className='row' key={index}>
                <label>{stat.period}</label>
                <label className='center'>{parseThousandSeparator(stat.SEND)}</label>
                <label className='center'>
                    {parseThousandSeparator(stat.OPEN)} ({calcPercentage(stat.OPEN, stat.SEND)}%)
                </label>
                <label className='center'>
                    {parseThousandSeparator(stat.CLICK)} (
                    <span style={getCTRStyle(Number(ctrPercentage))}>{ctrPercentage}%</span>)
                </label>
            </div>
        );
    }

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='email-step-stats default-popup'>
                <div className='header-container'>
                    <label className='header-title'>{t('email.stats.title', 'E-mail statistieken')}</label>
                    <CloseButton onClick={onClose} />
                </div>
                <div className='body-container'>
                    <div className='filter-container'>
                        <BluemateSelect
                            value={splitMethod}
                            onChange={onChangeSplitMethod}
                            items={methodItems}
                            label={t('email.stats.display', 'Weergave')}
                            className='display-select'
                        />
                        <BluemateButton
                            onClick={onClickPeriod}
                            label={t('email.stats.adjustPeriod', 'Periode aanpassen')}
                            secondary
                        />
                    </div>
                    {renderStats()}
                </div>
            </div>
            <PeriodPicker onSubmit={onChangePeriod} period={period} ref={periodPicker} />
        </WithBackground>
    );
}
